/*
  eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return,
  import/no-cycle, no-plusplus, no-param-reassign, consistent-return, no-shadow
  */
import isNil from 'lodash/isNil';
import isEqual from 'lodash/isEqual';
import isNumber from 'lodash/isNumber';
import { action, Action, computed, Computed, createContextStore, Thunk, thunk } from 'easy-peasy';
import { v4 as uuidv4 } from 'uuid';
import isUndefined from 'lodash/isUndefined';
import { AxiosResponse } from 'axios';
import { TFunction } from 'react-i18next';
import { createNewSchema } from '../formBuilder/utils/createNewSchema';
import { Branch, LANGUAGE_CODE, QUESTIONNAIRE_ELEMENT_TYPE, TEMPLATE_MODE } from '../questionnaireTemplate.enums';
import {
  CreateQuestionnaireTemplateResponse,
  DenormalizedElement,
  MultiChoiceOption,
  QuestionnaireDragSource,
  QuestionnaireDropZone,
  QuestionnaireElementPath,
  QuestionnaireElementPathValue,
  QuestionnaireEntities,
  QuestionnaireFormElement,
  QuestionnaireOrdering,
  QuestionnaireUI,
  TemplateSnapshot,
  UpdateQuestionnaireElementPayload,
} from '../questionnaireTemplate.types';
import { HttpStoreModel } from '../../../../http/models/httpModel';
import { QuestionnaireTemplateController } from './questionnaireTemplateController';
import HttpClientWithRetry from '../../../../http/client/HttpClientWithRetry';
import { denormalizeQuestionnaire } from '../formBuilder/utils/denormalizeElements';
import { payloadSchema } from '../formBuilder/errorHandlers/formValidation.schema';
import { BackendFieldError } from '../formBuilder/errorHandlers/parseBackendErrors';
import { normalizeTemplate } from '../formBuilder/utils/normalizeElements';
import { getEntitiesWithEditedFlag } from '../formBuilder/utils/getEntitiesWithEditedFlag';

const initialEntities = { elements: {} };
const initialOrdering = { root: [] };
const initialUI = {
  draggedElementId: null,
  dragSource: null,
  hoveredDropZone: null,
  selectedElementId: null,
};

const getNestedValue = (obj: any, path: string) => {
  return path.split('.').reduce((acc, part) => {
    return acc && !isUndefined(acc[part]) ? acc[part] : undefined;
  }, obj);
};

type QuestionnaireTemplateError = { message: string; context?: Record<string, unknown> };

export interface QuestionnaireTemplateModel {
  // STATE
  entities: QuestionnaireEntities;
  ordering: QuestionnaireOrdering;
  ui: QuestionnaireUI;
  errors: Record<string, QuestionnaireTemplateError>;
  templateName: string;
  selectedLanguage: LANGUAGE_CODE;
  selectedElementId: string | null;
  isCreatingTemplate: boolean;
  isUpdatingTemplate: boolean;
  isLoading: boolean;
  templateMode: TEMPLATE_MODE;
  activePositionCount: number | null;

  savedSnapshot: TemplateSnapshot | null;

  // COMPUTED (Selectors)
  rootElementIds: Computed<QuestionnaireTemplateModel, string[]>;
  rootElements: Computed<QuestionnaireTemplateModel, QuestionnaireFormElement[]>;
  getChildIds: Computed<QuestionnaireTemplateModel, (parentId: string, branch: Branch) => string[]>;
  getChildren: Computed<
    QuestionnaireTemplateModel,
    (parentId: string | null, branch: Branch | null) => QuestionnaireFormElement[]
  >;
  isParent: Computed<QuestionnaireTemplateModel, (id: string) => boolean>;
  getElementValue: Computed<
    QuestionnaireTemplateModel,
    <P extends string>(
      id: string,
      path: P & QuestionnaireElementPath<QuestionnaireFormElement>,
    ) => QuestionnaireElementPathValue<QuestionnaireFormElement, P>
  >;
  getElementById: Computed<QuestionnaireTemplateModel, (id: string) => QuestionnaireFormElement | undefined>;
  getElementOptionIds: Computed<QuestionnaireTemplateModel, (id: string) => string[]>;
  elementLocationLookup: Computed<QuestionnaireTemplateModel>;
  getElementIndex: Computed<QuestionnaireTemplateModel, (id: string) => { index: number; parentIndex: number }>;
  setIsCreatingTemplate: Action<QuestionnaireTemplateModel, boolean>;
  setIsUpdatingTemplate: Action<QuestionnaireTemplateModel, boolean>;
  hasErrors: Computed<QuestionnaireTemplateModel, boolean>;
  isDirty: Computed<QuestionnaireTemplateModel, boolean>;
  // ACTIONS
  setTemplateName: Action<QuestionnaireTemplateModel, string>;
  setSelectedLanguage: Action<QuestionnaireTemplateModel, LANGUAGE_CODE>;
  addElementToRoot: Action<QuestionnaireTemplateModel, QUESTIONNAIRE_ELEMENT_TYPE>;
  addElement: Action<
    QuestionnaireTemplateModel,
    { element: QuestionnaireFormElement; parentId?: string | null; branch?: Branch | null; index?: number | null }
  >;
  removeElement: Action<QuestionnaireTemplateModel, string>;
  updateElement: Action<QuestionnaireTemplateModel, { id: string; updates: Partial<Omit<QuestionnaireFormElement, 'id'>> }>;
  updateElementValue: Action<QuestionnaireTemplateModel, UpdateQuestionnaireElementPayload<any>>;
  setSelectedElementId: Action<QuestionnaireTemplateModel, string>;
  validatePayload: Action<
    QuestionnaireTemplateModel,
    {
      name: string;
      questionLocale: LANGUAGE_CODE;
      default: boolean;
      questions: DenormalizedElement[];
    }
  >;
  setActivePositionCount: Action<QuestionnaireTemplateModel, number | null>;

  // Reorder within same parent
  reorderElement: Action<
    QuestionnaireTemplateModel,
    { elementId: string; fromIndex: number; toIndex: number; parentId: string | null; branch: Branch | null }
  >;
  // Move to different parent/branch
  moveElement: Action<
    QuestionnaireTemplateModel,
    { elementId: string; newParentId: string | null; newBranch: Branch | null; newIndex: number }
  >;
  setDraggedElement: Action<QuestionnaireTemplateModel, { elementId: string; source: QuestionnaireDragSource } | null>;
  setHoveredDropZone: Action<QuestionnaireTemplateModel, QuestionnaireDropZone | null>;
  setSelectedElement: Action<QuestionnaireTemplateModel, string | null>;

  // branches / followups
  switchBranch: Action<QuestionnaireTemplateModel, { elementId: string; oldBranch: Branch; newBranch: Branch }>;
  activateBranch: Action<QuestionnaireTemplateModel, { elementId: string; branchName: Branch }>;
  removeBranch: Action<QuestionnaireTemplateModel, { elementId: string; branchName: Branch }>;

  // options
  updateOptionText: Action<QuestionnaireTemplateModel, { elementId: string; optionId: string; value: string }>;
  updateOptionIsPreferred: Action<QuestionnaireTemplateModel, { elementId: string; optionIds: string[]; value: boolean }>;
  addOption: Action<QuestionnaireTemplateModel, string>;
  removeOption: Action<QuestionnaireTemplateModel, { elementId: string; optionId: string }>;
  reorderOptions: Action<QuestionnaireTemplateModel, { elementId: string; oldIndex: number; newIndex: number }>;

  clearUI: Action<QuestionnaireTemplateModel, void>;

  // Load form from API
  loadForm: Action<
    QuestionnaireTemplateModel,
    {
      entities: QuestionnaireEntities;
      ordering: QuestionnaireOrdering;
      templateName?: string;
      selectedLanguage?: LANGUAGE_CODE;
    }
  >;
  setLoading: Action<QuestionnaireTemplateModel, boolean>;
  setTemplateMode: Action<QuestionnaireTemplateModel, TEMPLATE_MODE>;
  setFieldError: Action<QuestionnaireTemplateModel, { key: string; error: QuestionnaireTemplateError }>;
  clearFieldError: Action<QuestionnaireTemplateModel, string>;
  reset: Action<QuestionnaireTemplateModel, void>;
  setBackendErrors: Action<QuestionnaireTemplateModel, Record<string, string>>;

  commitSnapshot: Action<QuestionnaireTemplateModel, void>;
  clearSnapshot: Action<QuestionnaireTemplateModel, void>;

  // THUNKS
  saveTemplate: Thunk<
    QuestionnaireTemplateModel,
    { accountId: string; toast: (options: any) => void; t: TFunction<'questionnaireTemplate'>; templateId?: string },
    void,
    HttpStoreModel,
    Promise<AxiosResponse<CreateQuestionnaireTemplateResponse> | undefined>
  >;
  fetchTemplate: Thunk<
    QuestionnaireTemplateModel,
    { accountId: string; templateId: string; isCopy?: boolean },
    void,
    HttpStoreModel,
    Promise<void>
  >;
}

const questionnaireTemplateModel = (httpClient: HttpClientWithRetry): QuestionnaireTemplateModel => ({
  entities: { elements: {} },
  ordering: { root: [] },
  ui: { draggedElementId: null, dragSource: null, hoveredDropZone: null, selectedElementId: null },
  errors: {},
  templateName: '',
  selectedLanguage: LANGUAGE_CODE.EN,
  selectedElementId: null,
  isCreatingTemplate: false,
  isUpdatingTemplate: false,
  isLoading: false,
  templateMode: TEMPLATE_MODE.CREATE,
  activePositionCount: null,

  savedSnapshot: null,

  // COMPUTED (Selectors)
  rootElementIds: computed((state) => state.ordering.root),
  rootElements: computed((state) => {
    return state.ordering.root.map((id) => state.entities.elements[id]);
  }),
  getChildIds: computed([(state) => state.ordering], (ordering) => (parentId: string, branch: Branch) => {
    const key = `${parentId}-${branch}`;
    return ordering[key] ?? [];
  }),
  getChildren: computed((state) => {
    return (parentId, branch) => {
      const key = parentId && branch ? `${parentId}-${branch}` : 'root';
      const childrenIds = state.ordering[key] || /* istanbul ignore next */ [];
      return childrenIds.map((id) => state.entities.elements[id]);
    };
  }),
  isParent: computed((state) => (id: string) => {
    return Object.keys(state.ordering).some((key) => key.startsWith(id));
  }),
  getElementValue: computed((state) => (id: string, path: string) => {
    const element = state.entities.elements[id];
    if (!element) return undefined;
    return getNestedValue(element, path);
  }),
  getElementById: computed((state) => {
    return (id) => state.entities.elements[id];
  }),
  getElementOptionIds: computed([(state) => state.entities.elements], (elements) => {
    return (id: string) => {
      const options = elements[id]?.options;
      if (!options) return [];
      return options.map((option) => option.id);
    };
  }),
  elementLocationLookup: computed((state) => {
    const lookup: Record<string, { parentId: string | null; branch: string | null; key: string }> = {};

    Object.keys(state.ordering).forEach((key) => {
      const ids = state.ordering[key];
      const isRoot = key === 'root';
      const [parentId, branch] = isRoot ? [null, null] : key.split('-');

      ids.forEach((id) => {
        lookup[id] = { parentId, branch, key };
      });
    });

    return lookup;
  }),
  getElementIndex: computed(
    [(state) => state.elementLocationLookup, (state) => state.ordering],
    (lookup, ordering) => (id: string) => {
      const location = lookup[id];
      if (!location) return { index: -1, parentIndex: -1 };

      const list = ordering[location.key] || /* istanbul ignore next */ [];
      const currentIndex = list.indexOf(id);

      const lastHyphenIndex = location.key.lastIndexOf('-');
      const parentId = lastHyphenIndex !== -1 ? location.key.substring(0, lastHyphenIndex) : null;

      let parentIndex = -1;
      if (parentId) {
        const rootList = ordering.root /* istanbul ignore next */ || [];
        parentIndex = rootList.indexOf(parentId);
      }

      return { index: currentIndex, parentIndex };
    },
  ),

  setIsCreatingTemplate: action((state, isCreatingTemplate) => {
    state.isCreatingTemplate = isCreatingTemplate;
  }),
  setIsUpdatingTemplate: action((state, isUpdatingTemplate) => {
    state.isUpdatingTemplate = isUpdatingTemplate;
  }),
  hasErrors: computed((state) => Object.keys(state.errors).length > 0),

  isDirty: computed((state) => {
    if (!state.savedSnapshot) return false;

    return !isEqual(
      {
        entities: state.entities,
        ordering: state.ordering,
        templateName: state.templateName,
        selectedLanguage: state.selectedLanguage,
      },
      state.savedSnapshot,
    );
  }),

  setTemplateName: action((state, name) => {
    state.templateName = name;
  }),

  setSelectedLanguage: action((state, language) => {
    state.selectedLanguage = language;
  }),

  addElementToRoot: action((state, type: QUESTIONNAIRE_ELEMENT_TYPE) => {
    const newElement = createNewSchema(type);
    const newId = newElement.id;

    state.entities.elements[newId] = newElement;
    state.ordering.root.push(newId);

    delete state.errors.questions;
  }),
  addElement: action((state, payload) => {
    const { element, parentId = null, branch = null, index = null } = payload;
    //  Add to entities
    state.entities.elements[element.id] = {
      ...element,
      parentId,
      parentBranch: branch,
    };

    // Add to ordering
    const key = parentId && branch ? `${parentId}-${branch}` : 'root';
    if (!state.ordering[key]) {
      state.ordering[key] = [];
    }

    if (!isNil(index)) {
      state.ordering[key].splice(index, 0, element.id);
    } else {
      state.ordering[key].push(element.id);
    }
    delete state.errors.questions;
  }),
  removeElement: action((state, id) => {
    const element = state.entities.elements[id];
    if (!element) return;

    const cleanupRecursive = (targetId: string) => {
      const branches = element.activeBranches;

      branches?.forEach((branch) => {
        const key = `${targetId}-${branch}`;
        if (state.ordering[key]) {
          state.ordering[key].forEach((childId) => cleanupRecursive(childId));
          delete state.ordering[key];
        }
      });

      // Remove all errors for this element and its options
      Object.keys(state.errors)
        .filter((key) => key.startsWith(targetId))
        .forEach((key) => delete state.errors[key]);

      delete state.entities.elements[targetId];
    };

    const parentKey = element.parentId && element.parentBranch ? `${element.parentId}-${element.parentBranch}` : 'root';

    /* istanbul ignore else */
    if (state.ordering[parentKey]) {
      state.ordering[parentKey] = state.ordering[parentKey].filter((cid) => cid !== id);
    }

    cleanupRecursive(id);
  }),
  updateElement: action((state, payload) => {
    const { id, updates } = payload;
    if (state.entities.elements[id]) {
      state.entities.elements[id] = {
        ...state.entities.elements[id],
        ...updates,
        // Ensure ID cannot be changed via update
        id,
      };
    }
  }),
  updateElementValue: action((state, payload: { id: string; path: string; value: any }) => {
    const { id, path, value } = payload;
    const element = state.entities.elements[id];

    if (!element) return;

    // Split the path (e.g., 'config.placeholder' -> ['config', 'placeholder'])
    const parts = path.split('.');

    // Clone the element to maintain immutability
    const newElement = { ...element };
    let current: any = newElement;

    // Traverse to the second-to-last part
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      // Create nested object/array if it doesn't exist, and clone it
      current[part] = Array.isArray(current[part]) ? /* istanbul ignore next */ [...current[part]] : { ...current[part] };
      current = current[part];
    }

    // Set the final value
    const lastPart = parts[parts.length - 1];
    current[lastPart] = value;

    state.entities.elements[id] = newElement;
  }),
  setSelectedElementId: action((state, id) => {
    state.selectedElementId = id;
  }),

  validatePayload: action((state, payload) => {
    const newErrors: Record<string, { message: string; context?: Record<string, unknown> }> = {};

    const { error } = payloadSchema.validate(payload, { abortEarly: false });

    if (error) {
      error.details.forEach((detail) => {
        const { path } = detail;
        const errorValue = { message: detail.message, context: detail.context };

        if (path[0] === 'questions' && isNumber(path[1])) {
          const question = payload.questions[path[1]];
          if (question) {
            const fieldPath = path.slice(2);

            if (fieldPath[0] === 'options' && !isNil(fieldPath[1])) {
              const optionIndex = parseInt(String(fieldPath[1]), 10);
              const optionId = question.options?.[optionIndex]?.id;

              if (optionId) {
                const remainingPath = fieldPath.slice(2).join('.');
                const key = [question.id, 'options', optionId, remainingPath].filter(Boolean).join('.');
                newErrors[key] = errorValue;
              } else {
                const key = [question.id, fieldPath.join('.')].join('.');
                newErrors[key] = errorValue;
              }
            } else {
              const key = fieldPath.length ? `${question.id}.${fieldPath.join('.')}` : question.id;
              newErrors[key] = errorValue;
            }
          }
        } else {
          newErrors[path.join('.')] = errorValue;
        }
      });
    }

    state.errors = { ...state.errors, ...newErrors };
  }),

  setActivePositionCount: action((state, count) => {
    state.activePositionCount = count;
  }),

  reorderElement: action((state, payload) => {
    const { fromIndex, toIndex, parentId, branch } = payload;
    const key = parentId && branch ? `${parentId}-${branch}` : 'root';
    const list = state.ordering[key];

    /* istanbul ignore else */
    if (list) {
      const [movedItem] = list.splice(fromIndex, 1);
      list.splice(toIndex, 0, movedItem);
    }
  }),
  moveElement: action((state, payload) => {
    const { elementId, newParentId, newBranch, newIndex } = payload;
    const element = state.entities.elements[elementId];

    if (!element) return;

    // IDENTIFY SOURCE
    const oldParentId = element.parentId;
    const oldBranch = element.parentBranch;
    const oldKey = oldParentId ? `${oldParentId}-${oldBranch}` : 'root';

    // IDENTIFY DESTINATION
    const newKey = newParentId ? `${newParentId}-${newBranch}` : 'root';

    // Check if moving within the same list
    const isSameList = oldKey === newKey;

    // REMOVE FROM OLD POSITION
    const sourceList = state.ordering[oldKey];
    let oldIndex = -1;
    /* istanbul ignore else */
    if (sourceList) {
      oldIndex = sourceList.indexOf(elementId);
      /* istanbul ignore else */
      if (oldIndex !== -1) {
        sourceList.splice(oldIndex, 1);
      }
    }

    // UPDATE ELEMENT PROPERTIES
    element.parentId = newParentId;
    element.parentBranch = newBranch;

    // Ensure the destination array exists
    if (!state.ordering[newKey]) {
      state.ordering[newKey] = [];
    }

    // ADJUST INDEX IF MOVING WITHIN SAME LIST
    let adjustedIndex = newIndex;
    if (isSameList && oldIndex !== -1 && oldIndex < newIndex) {
      // If we're moving down in the same list, the index shifts down by 1
      // because we already removed the element
      adjustedIndex = newIndex - 1;
    }

    // INSERT AT NEW POSITION
    state.ordering[newKey].splice(adjustedIndex, 0, elementId);
  }),
  setDraggedElement: action((state, payload) => {
    if (payload === null) {
      state.ui.draggedElementId = null;
      state.ui.dragSource = null;
    } else {
      state.ui.draggedElementId = payload.elementId;
      state.ui.dragSource = payload.source;
    }
  }),

  setHoveredDropZone: action((state, payload) => {
    state.ui.hoveredDropZone = payload;
  }),

  setSelectedElement: action((state, payload) => {
    state.ui.selectedElementId = payload;
  }),

  // Branch / Followup
  switchBranch: action((state, { elementId, oldBranch, newBranch }) => {
    const element = state.entities.elements[elementId];
    if (!element) return;

    // Update the activeBranches array
    element.activeBranches = element?.activeBranches?.map((b) => (b === oldBranch ? newBranch : /* istanbul ignore next */ b));

    // Migrate the ordering key
    const oldKey = `${elementId}-${oldBranch}`;
    const newKey = `${elementId}-${newBranch}`;

    if (state.ordering[oldKey]) {
      state.ordering[newKey] = [...state.ordering[oldKey]];
      delete state.ordering[oldKey];

      // Update children's references
      state.ordering[newKey].forEach((childId) => {
        /* istanbul ignore else */
        if (state.entities.elements[childId]) {
          state.entities.elements[childId].parentBranch = newBranch;
        }
      });
    }
  }),
  activateBranch: action((state, { elementId, branchName }) => {
    const element = state.entities.elements[elementId];
    if (element) {
      if (!element.activeBranches) element.activeBranches = [];
      if (!element.activeBranches.includes(branchName)) {
        element.activeBranches.push(branchName);
      }
    }
  }),
  removeBranch: action((state, { elementId, branchName }) => {
    const element = state.entities.elements[elementId];
    if (!element) return;

    element.activeBranches = element?.activeBranches?.filter((b) => b !== branchName);

    const branchKey = `${elementId}-${branchName}`;
    const childrenIds = state.ordering[branchKey] || [];

    const cleanupRecursive = (targetId: string) => {
      const possibleBranches = ['yes', 'no'];
      possibleBranches.forEach((b) => {
        const key = `${targetId}-${b}`;
        if (state.ordering[key]) {
          state.ordering[key].forEach((childId) => cleanupRecursive(childId));
          delete state.ordering[key];
        }
      });
      delete state.entities.elements[targetId];
    };

    childrenIds.forEach(cleanupRecursive);

    delete state.ordering[branchKey];
  }),

  // 1. Update Option Text
  updateOptionText: action((state, { elementId, optionId, value }) => {
    const element = state.entities.elements[elementId];
    if (element && element.options) {
      const option = element.options.find((o) => o.id === optionId);
      if (option) {
        option.text = value;
      }
    }
  }),

  updateOptionIsPreferred: action((state, { elementId, optionIds, value }) => {
    const element = state.entities.elements[elementId];
    if (element && element.options) {
      element.options.forEach((o) => {
        if (optionIds.includes(o.id)) {
          o.isPreferredAnswer = value;
        } else {
          o.isPreferredAnswer = false;
        }
      });
    }
  }),

  // 2. Add New Option
  addOption: action((state, elementId) => {
    const element = state.entities.elements[elementId];
    if (element) {
      if (!element.options) element.options = [];

      const newOption: MultiChoiceOption = {
        id: uuidv4(),
        text: '', // Empty initialized localized string
      };

      element.options.push(newOption);
    }
  }),

  // 3. Remove Option (with Minimum 2 Guard)
  removeOption: action((state, { elementId, optionId }) => {
    const element = state.entities.elements[elementId];
    if (element && element.options && element.options.length > 2) {
      element.options = element.options.filter((o) => o.id !== optionId);

      if (isNumber(element.totalAnswer)) {
        element.totalAnswer = Math.min(element.totalAnswer, element.options.length);
      }
    }
  }),

  // 4. Reorder Options (Internal Swap)
  reorderOptions: action((state, { elementId, oldIndex, newIndex }) => {
    const element = state.entities.elements[elementId];
    if (!element || !element.options) return;

    // Ensure indices are within bounds to prevent null/undefined insertions
    if (newIndex < 0 || newIndex >= element.options.length) return;

    const { options } = element;
    // Use a standard move logic that doesn't leave gaps
    const [removed] = options.splice(oldIndex, 1);
    /* istanbul ignore else */
    if (removed) {
      options.splice(newIndex, 0, removed);
    }
  }),

  clearUI: action((state) => {
    state.ui.draggedElementId = null;
    state.ui.dragSource = null;
    state.ui.hoveredDropZone = null;
  }),
  loadForm: action((state, payload) => {
    state.entities = payload.entities;
    state.ordering = payload.ordering;
    state.ui = initialUI;
    if (payload.templateName !== undefined) {
      state.templateName = payload.templateName;
    }
    if (payload.selectedLanguage !== undefined) {
      state.selectedLanguage = payload.selectedLanguage;
    }

    state.savedSnapshot = {
      entities: state.entities,
      ordering: state.ordering,
      templateName: state.templateName,
      selectedLanguage: state.selectedLanguage,
    };
  }),
  setLoading: action((state, isLoading) => {
    state.isLoading = isLoading;
  }),
  setTemplateMode: action((state, mode) => {
    state.templateMode = mode;
  }),

  setFieldError: action((state, { key, error }) => {
    if (state.errors[key]?.message === error?.message) return;
    state.errors[key] = error;
  }),

  clearFieldError: action((state, key) => {
    delete state.errors[key];
  }),

  reset: action((state) => {
    state.entities = initialEntities;
    state.ordering = initialOrdering;
    state.ui = initialUI;
    state.errors = {};
    state.ui.draggedElementId = null;
    state.ui.dragSource = null;
    state.ui.hoveredDropZone = null;
    state.ui.selectedElementId = null;
    state.selectedElementId = null;
    state.isLoading = false;
    state.savedSnapshot = null;
    state.templateName = '';
  }),

  setBackendErrors: action((state, errorsMap: Record<string, string>) => {
    const converted = Object.fromEntries(Object.entries(errorsMap).map(([key, message]) => [key, { message }]));
    state.errors = { ...state.errors, ...converted };
  }),

  commitSnapshot: action((state) => {
    state.savedSnapshot = {
      entities: state.entities,
      ordering: state.ordering,
      templateName: state.templateName,
      selectedLanguage: state.selectedLanguage,
    };
  }),

  clearSnapshot: action((state) => {
    state.savedSnapshot = null;
  }),

  saveTemplate: thunk(async (actions, { accountId, toast, templateId, t }, { getState }) => {
    const { entities, ordering, templateName, selectedLanguage, templateMode, savedSnapshot } = getState();

    // 1. Stamp isEdited on normalized entities first
    const entitiesWithEdits = getEntitiesWithEditedFlag(
      entities,
      templateMode === TEMPLATE_MODE.EDIT ? savedSnapshot?.entities ?? null : null,
    );

    const questions = denormalizeQuestionnaire(ordering.root, entitiesWithEdits, ordering);
    const payload: {
      name: string;
      questionLocale: LANGUAGE_CODE;
      questions: DenormalizedElement[];
      default?: boolean;
    } = {
      name: templateName,
      questionLocale: selectedLanguage,
      questions,
      default: false,
    };

    if (templateMode === TEMPLATE_MODE.EDIT) {
      delete payload.default;
    }

    // Validate payload before sending
    actions.validatePayload({ name: templateName, questionLocale: selectedLanguage, default: false, questions });

    const { errors } = getState();
    if (Object.keys(errors).length > 0) {
      if (errors.questions) {
        toast({
          title: t(errors.questions.message),
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
      return;
    }

    const controller = new QuestionnaireTemplateController(httpClient);

    try {
      actions.setIsCreatingTemplate(true);
      if (templateMode === TEMPLATE_MODE.EDIT) {
        if (!templateId) {
          toast({
            title: 'Template not found',
            status: 'error',
            duration: 4000,
            isClosable: true,
          });
          return;
        }
        return await controller.updateTemplate(accountId, templateId, payload).then((res) => {
          actions.clearSnapshot();
          return res;
        });
      }
      return await controller.createTemplate(accountId, payload).then((res) => {
        actions.clearSnapshot();
        return res;
      });
    } catch (error: any) {
      const status = error?.response?.status;
      const backendErrors = error?.response?.data?.errors ?? [];

      if (status === 409) {
        actions.setFieldError({
          key: 'name',
          error: {
            message: error?.response?.data?.errors ?? /* istanbul ignore next */ 'A template with this name already exists.',
          },
        });
        throw error;
      }

      if (backendErrors.length > 0) {
        const byField: Record<string, string> = {};
        const { entities } = getState();

        backendErrors.forEach(({ questionId, path, message }: BackendFieldError) => {
          if (questionId) {
            const fieldPath = path.slice(2); // e.g. ['options', '0', 'text']

            // Map option index to optionId
            if (fieldPath[0] === 'options' && !isNil(fieldPath[1])) {
              const optionIndex = parseInt(String(fieldPath[1]), 10);
              const optionId = entities.elements[questionId]?.options?.[optionIndex]?.id;

              if (optionId) {
                const remainingPath = fieldPath.slice(2).join('.'); // e.g. 'text'
                const key = [questionId, 'options', optionId, remainingPath].filter(Boolean).join('.');
                byField[key] = message;
              } else {
                // fallback to index-based if optionId not found
                const key = [questionId, fieldPath.join('.')].join('.');
                byField[key] = message;
              }
            } else {
              const key = [questionId, fieldPath.join('.')].filter(Boolean).join('.');
              byField[key] = message;
            }
          } else {
            byField[path.join('.')] = message;
          }
        });

        actions.setBackendErrors(byField);
      }

      throw error;
    } finally {
      actions.setIsCreatingTemplate(false);
    }
  }),

  fetchTemplate: thunk(async (actions, { accountId, templateId, isCopy = false }) => {
    actions.setLoading(true);
    actions.setTemplateMode(isCopy ? TEMPLATE_MODE.COPY : TEMPLATE_MODE.EDIT);
    try {
      const controller = new QuestionnaireTemplateController(httpClient);

      const response = await controller.getTemplateById(accountId, templateId);
      const apiTemplate = response.data;

      let resolvedName = apiTemplate.name ?? '';
      if (isCopy) {
        const nameResponse = await controller.validateDuplicateName(accountId, resolvedName);
        resolvedName = nameResponse.data?.suggestedName ?? resolvedName;
      }

      /* istanbul ignore next */
      if (apiTemplate?.positionCount) {
        actions.setActivePositionCount(apiTemplate.positionCount as number);
      }

      const { entities, ordering, selectedLanguage } = normalizeTemplate({ ...apiTemplate, name: resolvedName }, isCopy);

      actions.loadForm({
        entities,
        ordering,
        templateName: resolvedName,
        selectedLanguage: selectedLanguage as LANGUAGE_CODE,
      });
    } finally {
      actions.setLoading(false);
    }
  }),
});

export default questionnaireTemplateModel;

export const QuestionnaireTemplateStore = createContextStore<QuestionnaireTemplateModel>(
  /* istanbul ignore next */
  (initialData: { httpClient: HttpClientWithRetry }) => questionnaireTemplateModel(initialData.httpClient),
  { defaultState: { httpClient: null } },
);
